import { Button, Collapse, DatePicker, Empty, Modal, Popconfirm, Table } from "antd";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { format } from "date-fns";
import locale from "antd/es/date-picker/locale/pt_BR";
import { api } from "../../../../config";
import { AuthenticationState } from "../../../../interfaces/users";
import { FieldReportViewModel } from "../../../../interfaces";
import { formatCurrency } from "../../../../utils";
import { ModulePermission, userHasPermission } from "../../../../utils/helpers/userHelpers";
import { SearchAssemblers } from "../../../Shared/Components/SearchAssemblers/SearchAssemblers";
import "./style.css";

interface PaymentProps {
  refetch?: () => Promise<unknown>;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

type TicketGroup = { ownerId: number | null; name: string; tickets: FieldReportViewModel[] };

const getAssemblerTotalValue = (tickets: FieldReportViewModel[]) =>
  tickets.reduce((total, ticket) => total +
    ticket.products.reduce((sum, product) => sum + Number(product.value ?? 0), 0) +
    (ticket.PaymentAdditions ?? []).reduce((sum, addition) =>
      String(addition.status) === "APPROVED" ? sum + Number(addition.value ?? 0) : sum, 0), 0);

export const Payment: React.FC<PaymentProps> = ({ refetch, isOpen, onClose }) => {
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const [groups, setGroups] = useState<TicketGroup[]>([]);
  const [assembler, setAssembler] = useState<number>();
  const [period, setPeriod] = useState<{ initial?: string; final?: string }>({});
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [searched, setSearched] = useState(false);
  const busy = useRef(false);
  const searchVersion = useRef(0);
  const canApprove = userHasPermission(user, { payments: ModulePermission.ADMIN });

  const invalidateResults = () => {
    searchVersion.current += 1;
    setGroups([]);
    setSearched(false);
  };

  const handleSearch = async () => {
    const version = ++searchVersion.current;
    setLoading(true);
    setGroups([]);
    setSearched(false);
    try {
      const params = new URLSearchParams({
        status: "ASSEMBLE_APPROVED", forPayment: "1", pageSize: "10000",
      });
      if (assembler) params.set("ownerId", String(assembler));
      if (period.initial && period.final) {
        params.set("startDate", period.initial);
        params.set("endDate", period.final);
      }
      const response = await api(token).get(`tickets/assemble/?${params}`);
      if (version !== searchVersion.current) return;
      const grouped = new Map<number | null, TicketGroup>();
      (response.tickets as FieldReportViewModel[]).forEach(ticket => {
        const ownerId = ticket.ownerId ?? ticket.owner?.id ?? null;
        const group = grouped.get(ownerId) ?? {
          ownerId, name: ticket.owner?.fullName ?? (ownerId ? `Montador ${ownerId}` : "Sem montador"), tickets: [],
        };
        group.tickets.push(ticket);
        grouped.set(ownerId, group);
      });
      setGroups(Array.from(grouped.values()));
      setSearched(true);
    } catch {
      if (version === searchVersion.current) toast.error("Ocorreu um erro ao buscar os tickets");
    } finally {
      if (version === searchVersion.current) setLoading(false);
    }
  };

  const approveGroup = async (group: TicketGroup) => {
    if (busy.current || !group.ownerId || !canApprove) return;
    busy.current = true;
    setApproving(true);
    let approved = false;
    try {
      await api(token).post("tickets/assemble/bulk-approve-payments", {
        ownerId: group.ownerId,
        fieldReportIds: group.tickets.map(ticket => ticket.id),
      });
      approved = true;
      setGroups(current => current.filter(item => item.ownerId !== group.ownerId));
      toast.success("Tickets aprovados com sucesso!");
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? "Ocorreu um erro ao aprovar os tickets");
      invalidateResults();
    } finally {
      // Refresh even on failure: another user may have changed the tickets.
      try { await refetch?.(); } catch {
        toast.error(approved
          ? "A aprovação foi concluída, mas não foi possível atualizar o kanban. Atualize a página."
          : "Não foi possível atualizar o kanban. Atualize a página.");
      }
      busy.current = false;
      setApproving(false);
    }
  };

  const handleClose = () => {
    if (busy.current) return;
    invalidateResults();
    setAssembler(undefined);
    setPeriod({});
    setLoading(false);
    onClose();
  };

  return (
    <Modal width={1000} title="Aprovação de pagamento por montador" open={isOpen}
      footer={null} onCancel={handleClose} closable={!approving} maskClosable={!approving} keyboard={!approving}>
      <div className="flex flex-col gap-4 py-2">
        <div className="flex gap-4">
          <SearchAssemblers assembler={assembler} disabled={loading || approving}
            setAssembler={value => { setAssembler(value); invalidateResults(); }} />
          <DatePicker.RangePicker className="w-full" locale={locale} disabled={loading || approving}
            value={period.initial && period.final ? [dayjs(period.initial), dayjs(period.final)] : null}
            format="DD/MM/YYYY" onChange={dates => {
              setPeriod(dates?.[0] && dates?.[1]
                ? { initial: dates[0].format("YYYY-MM-DD"), final: dates[1].format("YYYY-MM-DD") } : {});
              invalidateResults();
            }} />
          <Button loading={loading} disabled={approving} onClick={handleSearch}>Buscar Tickets</Button>
        </div>
        {searched && !groups.length && <Empty description="Nenhum ticket aguardando aprovação para os filtros selecionados" />}
        <Collapse items={groups.map(group => ({
          key: group.ownerId ?? "unassigned",
          label: <div className="flex w-full justify-between gap-4">
            <span>Montador: {group.name} - Total: {formatCurrency(getAssemblerTotalValue(group.tickets))}</span>
          </div>,
          extra: <div onClick={event => event.stopPropagation()}>
            <Popconfirm title="Confirmar aprovação?"
              description={`Aprovar ${group.tickets.length} ticket(s) de ${group.name}, no total de ${formatCurrency(getAssemblerTotalValue(group.tickets))}? Os tickets serão enviados para Pagamento efetuado.`}
              okText="Aprovar" cancelText="Cancelar" onConfirm={() => approveGroup(group)}
              disabled={!canApprove || !group.ownerId || approving || loading}
              okButtonProps={{ loading: approving }}>
              <Button type="primary" disabled={!canApprove || !group.ownerId || approving || loading}>Aprovar</Button>
            </Popconfirm>
          </div>,
          children: <Table bordered dataSource={group.tickets} rowKey="id" pagination={false}>
            <Table.Column title="Título" dataIndex="title" />
            <Table.Column title="Data Montagem" dataIndex="EventsAssemble" render={events =>
              events?.[0]?.start ? format(new Date(events[0].start), "dd/MM/yyyy") : "Não informada"} />
            <Table.Column title="Valor da Montagem" render={(_, ticket: FieldReportViewModel) =>
              formatCurrency(getAssemblerTotalValue([ticket]))} />
          </Table>,
        }))} />
        <Button onClick={handleClose} disabled={approving}>Cancelar</Button>
      </div>
    </Modal>
  );
};
